import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Design tokens (mint green, matches Auth.js) ───────────────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

// ── Helpers ───────────────────────────────────────────────────────────────────

function pad2(n) { return String(Math.max(0, n)).padStart(2, '0'); }
function pad3(n) { return String(Math.abs(n)).padStart(3, '0'); }

function getMnemonic(code) {
  if (code === 0)   return 'HLT';
  if (code === 901) return 'INP';
  if (code === 902) return 'OUT';
  const op = Math.floor(code / 100);
  const map = { 1: 'ADD', 2: 'SUB', 3: 'STA', 5: 'LDA', 6: 'BRA', 7: 'BRZ', 8: 'BRP' };
  return map[op] || '';
}

// ── Assembler ─────────────────────────────────────────────────────────────────

const OPCODES    = { HLT: 0, ADD: 100, SUB: 200, STA: 300, LDA: 500, BRA: 600, BRZ: 700, BRP: 800 };
const NO_OPERAND = { INP: 901, OUT: 902 };
const ALL_MNEMONICS = new Set([...Object.keys(OPCODES), ...Object.keys(NO_OPERAND), 'DAT']);

function assemble(source) {
  const rawLines = source.split('\n');
  const labels = {};
  const parsed = [];

  // Pass 1 — collect labels and determine addresses
  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].replace(/\/\/.*/, '').trim();
    if (!line) continue;

    const tokens = line.split(/\s+/);
    let ti = 0;
    let label = null;

    if (tokens[ti] && !ALL_MNEMONICS.has(tokens[ti].toUpperCase())) {
      label = tokens[ti].toUpperCase();
      ti++;
    }

    if (ti >= tokens.length) continue;

    const mnemonic = tokens[ti].toUpperCase();
    const operand  = tokens[ti + 1] || null;
    const addr     = parsed.length;

    if (label) {
      if (labels[label] !== undefined) return { error: `Line ${i + 1}: duplicate label '${label}'` };
      labels[label] = addr;
    }

    if (!ALL_MNEMONICS.has(mnemonic)) return { error: `Line ${i + 1}: unknown mnemonic '${mnemonic}'` };
    if (parsed.length >= 100) return { error: `Line ${i + 1}: program exceeds 100 mailboxes` };

    parsed.push({ mnemonic, operand, lineNum: i + 1 });
  }

  // Pass 2 — emit
  const memory  = new Array(100).fill(0);
  const listing = [];

  for (let addr = 0; addr < parsed.length; addr++) {
    const { mnemonic, operand, lineNum } = parsed[addr];

    if (mnemonic === 'DAT') {
      const val = operand ? parseInt(operand, 10) : 0;
      if (isNaN(val) || val < 0 || val > 999) return { error: `Line ${lineNum}: DAT value out of range` };
      memory[addr] = val;
      listing.push({ addr, mnemonic: 'DAT', operandStr: operand ?? '0', value: val });
      continue;
    }

    if (mnemonic in NO_OPERAND) {
      memory[addr] = NO_OPERAND[mnemonic];
      listing.push({ addr, mnemonic, operandStr: '', value: NO_OPERAND[mnemonic] });
      continue;
    }

    if (mnemonic === 'HLT') {
      memory[addr] = 0;
      listing.push({ addr, mnemonic: 'HLT', operandStr: '', value: 0 });
      continue;
    }

    if (!operand) return { error: `Line ${lineNum}: ${mnemonic} requires an operand` };

    const asNum = parseInt(operand, 10);
    let operandAddr;
    if (!isNaN(asNum)) {
      operandAddr = asNum;
    } else {
      const key = operand.toUpperCase();
      if (labels[key] === undefined) return { error: `Line ${lineNum}: unknown label '${operand}'` };
      operandAddr = labels[key];
    }

    if (operandAddr < 0 || operandAddr > 99) return { error: `Line ${lineNum}: address ${operandAddr} out of range` };

    const value = OPCODES[mnemonic] + operandAddr;
    memory[addr] = value;
    listing.push({ addr, mnemonic, operandStr: pad2(operandAddr), value });
  }

  return { memory, listing, error: null };
}

// ── Machine state ─────────────────────────────────────────────────────────────

function makeInitialState(memory, inputQueue) {
  return {
    acc: 0, pc: 0, mar: 0, mdr: 0, ir: 0,
    negativeFlag: false,
    memory: [...memory],
    inputQueue: [...inputQueue],
    outputLog: [],
    halted: false,
  };
}

// ── Micro-step engine ─────────────────────────────────────────────────────────

function generateMicroSteps(initialMemory, inputQueue) {
  const steps     = [];
  const snapshots = [];

  let st = makeInitialState(initialMemory, inputQueue);
  const snap = s => ({ ...s, memory: [...s.memory], inputQueue: [...s.inputQueue], outputLog: [...s.outputLog] });

  snapshots.push(snap(st));

  const push = (stage, narration, highlights, busActivity, mutate) => {
    const next = mutate(snap(st));
    st = next;
    steps.push({ stage, narration, highlights, busActivity });
    snapshots.push(snap(st));
  };

  let guard = 5000;

  while (!st.halted && guard-- > 0) {
    // ── FETCH ────────────────────────────────────────────────────────────────

    // 1. PC -> MAR
    push('FETCH',
      'The address in the PC is copied to the MAR.',
      ['reg-pc', 'reg-mar'], null,
      s => ({ ...s, mar: s.pc })
    );

    // 2. MAR -> address bus -> memory; READ on control bus
    push('FETCH',
      'The MAR places the address on the address bus, and the control unit sends a read signal on the control bus.',
      ['reg-mar', 'bus-address', 'bus-control', 'unit-cu'],
      { bus: 'address', direction: 'cpu-to-memory', payload: pad2(st.mar) },
      s => s
    );

    // 3. memory -> data bus -> MDR
    push('FETCH',
      'Memory returns the instruction along the data bus into the MDR.',
      ['reg-mdr', 'bus-data', `mem-${pad2(st.mar)}`],
      { bus: 'data', direction: 'memory-to-cpu', payload: pad3(st.memory[st.mar]) },
      s => ({ ...s, mdr: s.memory[s.mar] })
    );

    // 4. MDR -> IR
    push('FETCH',
      'The instruction is copied from the MDR into the IR.',
      ['reg-mdr', 'reg-ir'], null,
      s => ({ ...s, ir: s.mdr })
    );

    // 5. PC = PC + 1
    push('FETCH',
      'The PC is incremented to point at the next instruction.',
      ['reg-pc'], null,
      s => ({ ...s, pc: s.pc + 1 })
    );

    // ── DECODE ───────────────────────────────────────────────────────────────

    const irVal      = st.ir;
    const mnemonic   = getMnemonic(irVal);
    const operandAddr = irVal % 100;

    let decodeNarration;
    if (mnemonic === 'INP' || mnemonic === 'OUT') {
      decodeNarration = `The control unit identifies this as ${mnemonic === 'INP' ? 'an INP (input)' : 'an OUT (output)'} instruction.`;
    } else if (mnemonic === 'HLT') {
      decodeNarration = 'The control unit identifies this as a HLT (halt) instruction.';
    } else {
      decodeNarration = `The control unit splits the instruction in the IR into its opcode (${mnemonic}) and operand (mailbox ${pad2(operandAddr)}).`;
    }

    push('DECODE', decodeNarration, ['unit-cu', 'reg-ir'], null, s => s);

    // ── EXECUTE ──────────────────────────────────────────────────────────────

    if (mnemonic === 'HLT') {
      push('EXECUTE', 'The halt instruction stops the machine.', ['unit-cu'], null,
        s => ({ ...s, halted: true }));
      break;
    }

    if (mnemonic === 'INP') {
      push('EXECUTE',
        'An input value is read into the accumulator.',
        ['reg-acc', 'io-input'], null,
        s => ({ ...s, acc: s.inputQueue.length > 0 ? s.inputQueue[0] : 0, inputQueue: s.inputQueue.slice(1) })
      );

    } else if (mnemonic === 'OUT') {
      push('EXECUTE',
        'The value in the accumulator is sent to the output.',
        ['reg-acc', 'io-output'], null,
        s => ({ ...s, outputLog: [...s.outputLog, s.acc] })
      );

    } else if (mnemonic === 'ADD' || mnemonic === 'SUB' || mnemonic === 'LDA') {
      // operand -> MAR
      push('EXECUTE',
        `The operand address (${pad2(operandAddr)}) is loaded into the MAR.`,
        ['reg-mar', 'unit-cu'], null,
        s => ({ ...s, mar: operandAddr })
      );

      // MAR -> address bus -> memory + READ
      push('EXECUTE',
        `The MAR places address ${pad2(operandAddr)} on the address bus, and the control unit sends a read signal.`,
        ['reg-mar', 'bus-address', 'bus-control', `mem-${pad2(operandAddr)}`],
        { bus: 'address', direction: 'cpu-to-memory', payload: pad2(operandAddr) },
        s => s
      );

      // memory -> data bus -> MDR
      const memVal = st.memory[operandAddr];
      push('EXECUTE',
        `The value in mailbox ${pad2(operandAddr)} is read along the data bus into the MDR.`,
        ['reg-mdr', 'bus-data', `mem-${pad2(operandAddr)}`],
        { bus: 'data', direction: 'memory-to-cpu', payload: pad3(memVal) },
        s => ({ ...s, mdr: s.memory[s.mar] })
      );

      if (mnemonic === 'LDA') {
        push('EXECUTE',
          'The value from the MDR is loaded into the accumulator.',
          ['reg-mdr', 'reg-acc'], null,
          s => ({ ...s, acc: s.mdr })
        );
      } else {
        const verb = mnemonic === 'ADD' ? 'adds' : 'subtracts';
        push('EXECUTE',
          `The ALU ${verb} the MDR value and the accumulator, storing the result in the accumulator.`,
          ['unit-alu', 'reg-mdr', 'reg-acc'], null,
          s => {
            const result = mnemonic === 'ADD' ? s.acc + s.mdr : s.acc - s.mdr;
            return { ...s, acc: result, negativeFlag: result < 0 };
          }
        );
      }

    } else if (mnemonic === 'STA') {
      // operand -> MAR
      push('EXECUTE',
        `The operand address (${pad2(operandAddr)}) is loaded into the MAR.`,
        ['reg-mar', 'unit-cu'], null,
        s => ({ ...s, mar: operandAddr })
      );

      // ACC -> MDR
      push('EXECUTE',
        'The accumulator is copied to the MDR.',
        ['reg-acc', 'reg-mdr'], null,
        s => ({ ...s, mdr: s.acc })
      );

      // MDR -> data bus -> memory + WRITE
      push('EXECUTE',
        `The MDR is written along the data bus to mailbox ${pad2(operandAddr)}, with a write signal on the control bus.`,
        ['reg-mdr', 'bus-data', 'bus-control', `mem-${pad2(operandAddr)}`],
        { bus: 'data', direction: 'cpu-to-memory', payload: pad3(st.mdr) },
        s => {
          const mem = [...s.memory];
          mem[s.mar] = s.mdr;
          return { ...s, memory: mem };
        }
      );

    } else if (mnemonic === 'BRA') {
      push('EXECUTE',
        `The PC is set to ${pad2(operandAddr)}, branching unconditionally.`,
        ['unit-cu', 'reg-pc'], null,
        s => ({ ...s, pc: operandAddr })
      );

    } else if (mnemonic === 'BRZ') {
      const taken = st.acc === 0;
      push('EXECUTE',
        taken
          ? `The accumulator is zero, so the PC is set to ${pad2(operandAddr)} (branch taken).`
          : `The accumulator is not zero (${st.acc}), so execution continues at PC ${pad2(st.pc)} (branch not taken).`,
        ['unit-cu', 'reg-acc', 'reg-pc'], null,
        s => taken ? { ...s, pc: operandAddr } : s
      );

    } else if (mnemonic === 'BRP') {
      const taken = !st.negativeFlag;
      push('EXECUTE',
        taken
          ? `The negative flag is not set, so the PC is set to ${pad2(operandAddr)} (branch taken).`
          : `The negative flag is set (result was negative), so execution falls through to PC ${pad2(st.pc)}.`,
        ['unit-cu', 'reg-acc', 'reg-pc'], null,
        s => taken ? { ...s, pc: operandAddr } : s
      );

    } else {
      // Unknown — halt gracefully
      push('EXECUTE', `Unknown instruction (${pad3(irVal)}). Machine halted.`, ['unit-cu'], null,
        s => ({ ...s, halted: true }));
      break;
    }
  }

  return { steps, snapshots };
}

// ── Preloaded programs ────────────────────────────────────────────────────────

const PRELOADED = [
  {
    name: 'Add two numbers',
    code: 'INP\nSTA 99\nINP\nADD 99\nOUT\nHLT',
    desc: 'Enter two numbers; outputs their sum. Uses INP, STA (data bus write), ADD (data bus read) and OUT, so every bus direction runs once.',
    defaultInput: '5 12',
  },
  {
    name: 'Add with labels (no input)',
    code: 'LDA num1\nADD num2\nSTA result\nOUT\nHLT\nnum1 DAT 7\nnum2 DAT 5\nresult DAT 0',
    desc: 'Adds two stored values using labels, so the full cycle runs without any input.',
    defaultInput: '',
  },
  {
    name: 'Countdown (branch demo)',
    code: 'LDA count\nloop OUT\nSUB one\nBRP loop\nHLT\ncount DAT 3\none DAT 1',
    desc: 'Counts down from 3 to 0 using SUB and BRP, showing branching and the negative flag.',
    defaultInput: '',
  },
];

// ── SVG Diagram ───────────────────────────────────────────────────────────────

function CPUDiagram({ state, highlights, busActivity }) {
  const hl  = new Set(highlights || []);
  const ba  = busActivity || null;

  const W = 800, H = 420;
  // Zones
  const CPU_X = 5,   CPU_Y = 5, CPU_W = 248, CPU_H = H - 10;
  const MEM_X = 402, MEM_Y = 5, MEM_W = W - MEM_X - 5, MEM_H = H - 10;
  const BUS_MID = (CPU_X + CPU_W + MEM_X) / 2; // 327.5

  // Bus Y positions
  const ADDR_Y = 152, DATA_Y = 212, CTRL_Y = 272;

  // Bus active flags
  const addrOn = hl.has('bus-address');
  const dataOn = hl.has('bus-data');
  const ctrlOn = hl.has('bus-control');
  const dataDir = ba?.bus === 'data' ? ba.direction : null;

  // Signal label on control bus
  const ctrlSignal = ba?.direction === 'cpu-to-memory' && ba?.bus === 'data' ? 'WRITE' : 'READ';

  // Memory window: 10 cells around MAR
  const winStart  = Math.max(0, Math.min(90, state.mar - 4));
  const memWindow = Array.from({ length: 10 }, (_, i) => winStart + i).filter(a => a < 100);

  // Register box
  const Reg = ({ id, label, value, x, y, w = 110, h = 44, isAddr = false }) => {
    const on = hl.has(id);
    return (
      <g id={id} aria-label={`${label}: ${value}`}>
        <rect x={x} y={y} width={w} height={h} rx="5"
          fill={on ? '#e8f7ef' : B.surface}
          stroke={on ? B.green : 'rgba(60,45,30,0.18)'}
          strokeWidth={on ? 2 : 1}
        />
        <text x={x + 6} y={y + 12} fontSize="7.5" fill={on ? B.greenDark : B.faint}
          fontFamily={font} fontWeight="700">{label}</text>
        <text x={x + w / 2} y={y + h - 9} fontSize="15" fill={on ? B.greenDark : B.text}
          fontFamily={mono} fontWeight="700" textAnchor="middle">
          {isAddr ? pad2(value) : pad3(value)}
        </text>
      </g>
    );
  };

  // Unit box (CU / ALU)
  const Unit = ({ id, label, x, y, w = 110, h = 50 }) => {
    const on = hl.has(id);
    return (
      <g id={id}>
        <rect x={x} y={y} width={w} height={h} rx="5"
          fill={on ? B.greenSoft : '#f0ece4'}
          stroke={on ? B.green : 'rgba(60,45,30,0.15)'}
          strokeWidth={on ? 2 : 1}
        />
        <text x={x + w / 2} y={y + h / 2 + 4} fontSize="11" fill={on ? B.greenDark : B.muted}
          fontFamily={font} fontWeight="800" textAnchor="middle">{label}</text>
      </g>
    );
  };

  // Bus line
  const Bus = ({ id, y, active, label, arrowDir, payload }) => (
    <g id={id}>
      <text x={BUS_MID} y={y - 8} fontSize="8" fill={active ? B.greenDark : B.faint}
        fontFamily={font} fontWeight="700" textAnchor="middle">{label}</text>
      <line
        x1={CPU_X + CPU_W} y1={y} x2={MEM_X} y2={y}
        stroke={active ? B.green : 'rgba(60,45,30,0.18)'}
        strokeWidth={active ? 3 : 1.5}
        strokeDasharray={active ? 'none' : '4 3'}
        markerEnd={active && arrowDir === 'right' ? 'url(#arrowR)' : undefined}
        markerStart={active && arrowDir === 'left'  ? 'url(#arrowL)' : undefined}
      />
      {active && payload && (
        <text x={BUS_MID} y={y + 14} fontSize="11" fill={B.greenDark}
          fontFamily={mono} fontWeight="800" textAnchor="middle">{payload}</text>
      )}
    </g>
  );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', height: 'auto' }}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="CPU and memory diagram">

      <defs>
        <marker id="arrowR" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto">
          <path d="M0,1 L6,3.5 L0,6 Z" fill={B.green} />
        </marker>
        <marker id="arrowL" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto-start-reverse">
          <path d="M0,1 L6,3.5 L0,6 Z" fill={B.green} />
        </marker>
      </defs>

      {/* ── CPU box ───────────────────────────────────────────────── */}
      <rect x={CPU_X} y={CPU_Y} width={CPU_W} height={CPU_H} rx="10"
        fill={B.surface} stroke="rgba(60,45,30,0.14)" strokeWidth="1.5" />
      <text x={CPU_X + CPU_W / 2} y={CPU_Y + 18} fontSize="9.5" fill={B.muted}
        fontFamily={font} fontWeight="800" textAnchor="middle" letterSpacing="1.5">CPU</text>

      {/* Units */}
      <Unit id="unit-cu"  label="Control Unit" x={CPU_X + 10} y={CPU_Y + 24}  w={108} h={50} />
      <Unit id="unit-alu" label="ALU"           x={CPU_X + 128} y={CPU_Y + 24} w={108} h={50} />

      {/* Registers: PC and MAR */}
      <Reg id="reg-pc"  label="PC"  value={state.pc}  x={CPU_X + 10}  y={92}  w={108} isAddr />
      <Reg id="reg-mar" label="MAR" value={state.mar} x={CPU_X + 128} y={92}  w={108} isAddr />

      {/* Registers: MDR and IR */}
      <Reg id="reg-mdr" label="MDR" value={state.mdr} x={CPU_X + 10}  y={148} w={108} />
      <Reg id="reg-ir"  label="IR"  value={state.ir}  x={CPU_X + 128} y={148} w={108} />

      {/* Accumulator (full width) */}
      <Reg id="reg-acc" label="Accumulator (AC)" value={state.acc} x={CPU_X + 10} y={206} w={226} h={50} />

      {/* Negative flag */}
      <g>
        <rect x={CPU_X + 10} y={272} width={108} height={28} rx="4"
          fill={state.negativeFlag ? '#fef2f2' : B.bg}
          stroke={state.negativeFlag ? '#fca5a5' : 'rgba(60,45,30,0.14)'} strokeWidth="1" />
        <text x={CPU_X + 16} y={282} fontSize="7.5" fill={B.faint} fontFamily={font} fontWeight="700">NEG FLAG</text>
        <text x={CPU_X + 64} y={293} fontSize="12" fill={state.negativeFlag ? '#dc2626' : B.muted}
          fontFamily={mono} fontWeight="700" textAnchor="middle">
          {state.negativeFlag ? '1' : '0'}
        </text>
      </g>

      {/* I/O panels */}
      <g id="io-input">
        <rect x={CPU_X + 128} y={272} width={108} height={28} rx="4"
          fill={hl.has('io-input') ? B.greenSoft : B.bg}
          stroke={hl.has('io-input') ? B.green : 'rgba(60,45,30,0.14)'}
          strokeWidth={hl.has('io-input') ? 2 : 1}
        />
        <text x={CPU_X + 182} y={290} fontSize="9" fill={hl.has('io-input') ? B.greenDark : B.faint}
          fontFamily={font} fontWeight="700" textAnchor="middle">← INPUT</text>
      </g>
      <g id="io-output">
        <rect x={CPU_X + 128} y={308} width={108} height={28} rx="4"
          fill={hl.has('io-output') ? B.greenSoft : B.bg}
          stroke={hl.has('io-output') ? B.green : 'rgba(60,45,30,0.14)'}
          strokeWidth={hl.has('io-output') ? 2 : 1}
        />
        <text x={CPU_X + 182} y={326} fontSize="9" fill={hl.has('io-output') ? B.greenDark : B.faint}
          fontFamily={font} fontWeight="700" textAnchor="middle">OUTPUT →</text>
      </g>

      {/* ── Bus zone ──────────────────────────────────────────────── */}
      <Bus id="bus-address" y={ADDR_Y} active={addrOn}
        label="Address bus"
        arrowDir="right"
        payload={ba?.bus === 'address' ? ba.payload : null}
      />
      <Bus id="bus-data" y={DATA_Y} active={dataOn}
        label="Data bus"
        arrowDir={dataDir === 'cpu-to-memory' ? 'right' : 'left'}
        payload={ba?.bus === 'data' ? ba.payload : null}
      />
      <Bus id="bus-control" y={CTRL_Y} active={ctrlOn}
        label="Control bus"
        arrowDir="right"
        payload={ctrlOn ? ctrlSignal : null}
      />

      {/* ── Memory box ────────────────────────────────────────────── */}
      <rect x={MEM_X} y={MEM_Y} width={MEM_W} height={MEM_H} rx="10"
        fill={B.surface} stroke="rgba(60,45,30,0.14)" strokeWidth="1.5" />
      <text x={MEM_X + MEM_W / 2} y={MEM_Y + 18} fontSize="9.5" fill={B.muted}
        fontFamily={font} fontWeight="800" textAnchor="middle" letterSpacing="1.5">
        MEMORY: mailboxes {pad2(winStart)}-{pad2(Math.min(99, winStart + 9))}
      </text>

      {/* Memory cells — 2 columns, 5 rows = 10 cells */}
      {memWindow.map((addr, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const cx  = MEM_X + 10 + col * 188;
        const cy  = MEM_Y + 28 + row * 38;
        const isActive = hl.has(`mem-${pad2(addr)}`);
        const isMAR    = addr === state.mar;
        const mnem     = getMnemonic(state.memory[addr]);
        return (
          <g key={addr} id={`mem-${pad2(addr)}`}>
            <rect x={cx} y={cy} width={180} height={30} rx="4"
              fill={isActive ? '#e8f7ef' : isMAR ? '#fdf8f0' : B.bg}
              stroke={isActive ? B.green : isMAR ? '#d4956a' : 'rgba(60,45,30,0.12)'}
              strokeWidth={isActive ? 2 : 1}
            />
            <text x={cx + 7} y={cy + 11} fontSize="7.5" fill={isActive ? B.greenDark : B.faint}
              fontFamily={mono} fontWeight="600">{pad2(addr)}</text>
            <text x={cx + 90} y={cy + 21} fontSize="13" fill={isActive ? B.greenDark : B.text}
              fontFamily={mono} fontWeight="700" textAnchor="middle">
              {pad3(state.memory[addr])}
            </text>
            <text x={cx + 172} y={cy + 21} fontSize="8.5" fill={isActive ? B.greenDark : B.faint}
              fontFamily={font} fontWeight="600" textAnchor="end">
              {mnem || ''}
            </text>
          </g>
        );
      })}

      <text x={MEM_X + MEM_W / 2} y={H - 8} fontSize="7.5" fill={B.faint}
        fontFamily={font} textAnchor="middle">Window follows MAR · {memWindow.length} of 100 mailboxes shown</text>
    </svg>
  );
}

// ── Stage indicator ───────────────────────────────────────────────────────────

function StageIndicator({ stage }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      {['FETCH', 'DECODE', 'EXECUTE'].map(s => (
        <span key={s} style={{
          padding: '2px 10px', borderRadius: 5, fontSize: '0.7rem',
          fontWeight: 800, fontFamily: font, letterSpacing: '0.4px',
          background: stage === s ? B.green : B.greenSoft,
          color: stage === s ? '#fff' : B.faint,
          transition: 'background 0.18s, color 0.18s',
        }}>{s}</span>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LMCSimulator({ onBack }) {
  const [presetIdx,      setPresetIdx]      = useState(0);
  const [source,         setSource]         = useState(PRELOADED[0].code);
  const [inputText,      setInputText]      = useState(PRELOADED[0].defaultInput);
  const [assembled,      setAssembled]      = useState(null);
  const [assembleError,  setAssembleError]  = useState('');
  const [steps,          setSteps]          = useState([]);
  const [snapshots,      setSnapshots]      = useState([]);
  const [stepIdx,        setStepIdx]        = useState(0);
  const [running,        setRunning]        = useState(false);
  const [speed,          setSpeed]          = useState(700); // ms per step

  const timerRef = useRef(null);

  const currentState = snapshots[stepIdx]     || null;
  const currentStep  = steps[stepIdx - 1]    || null; // step that produced snapshots[stepIdx]

  // ── Assemble ──────────────────────────────────────────────────────────────

  const handleAssemble = useCallback(() => {
    setRunning(false);
    const result = assemble(source);
    if (result.error) {
      setAssembleError(result.error);
      setAssembled(null);
      return;
    }
    setAssembleError('');
    setAssembled(result);

    const queue = inputText.trim()
      .split(/[\s,\n]+/).filter(Boolean)
      .map(Number).filter(n => !isNaN(n));

    const { steps: s, snapshots: ss } = generateMicroSteps(result.memory, queue);
    setSteps(s);
    setSnapshots(ss);
    setStepIdx(0);
  }, [source, inputText]);

  // ── Step controls ─────────────────────────────────────────────────────────

  const canStep   = !!steps.length && stepIdx < steps.length && !currentState?.halted;
  const canBack   = stepIdx > 0;
  const isHalted  = !!currentState?.halted;

  const doMicroStep = useCallback(() => {
    setStepIdx(i => Math.min(i + 1, steps.length));
  }, [steps.length]);

  const doInstrStep = useCallback(() => {
    setStepIdx(i => {
      let next = i + 1;
      // advance until the start of the next FETCH (or end)
      while (next < steps.length && steps[next]?.stage !== 'FETCH') next++;
      return Math.min(next, steps.length);
    });
  }, [steps]);

  const doBack  = useCallback(() => setStepIdx(i => Math.max(0, i - 1)), []);
  const doReset = useCallback(() => { setRunning(false); setStepIdx(0); }, []);

  // ── Auto-run ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!running) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setStepIdx(i => {
        if (i >= steps.length) { setRunning(false); return i; }
        return i + 1;
      });
    }, speed);
    return () => clearInterval(timerRef.current);
  }, [running, speed, steps.length]);

  useEffect(() => { if (isHalted) setRunning(false); }, [isHalted]);

  // ── Preset selection ──────────────────────────────────────────────────────

  const selectPreset = (idx) => {
    setPresetIdx(idx);
    setSource(PRELOADED[idx].code);
    setInputText(PRELOADED[idx].defaultInput);
    setAssembled(null);
    setSteps([]);
    setSnapshots([]);
    setStepIdx(0);
    setRunning(false);
    setAssembleError('');
  };

  // ── Button style helper ───────────────────────────────────────────────────

  const btn = (primary, disabled) => ({
    padding: '0.42rem 0.85rem',
    background: disabled ? 'transparent' : primary ? B.charcoal : B.surface,
    color: disabled ? B.faint : primary ? '#fff' : B.text,
    border: `1px solid ${disabled ? B.border : primary ? 'transparent' : B.borderStrong}`,
    borderRadius: 7, fontSize: '0.76rem', fontWeight: 700, fontFamily: font,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'background 0.12s',
    whiteSpace: 'nowrap',
  });

  // ── Render ────────────────────────────────────────────────────────────────

  const blankState = makeInitialState(new Array(100).fill(0), []);

  return (
    <div style={{ fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>

      {/* ── Top nav ───────────────────────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: B.surface, borderBottom: `1px solid ${B.border}`,
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '0 1.5rem', height: 60,
      }}>
        {onBack && (
          <button onClick={onBack} aria-label="Back" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
            fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
            alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={{ fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 }}>CPU Simulator</h1>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
          background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
        }}>A1.1.5</span>
        <span style={{
          fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
          background: '#e8eefb', color: '#4a63b0', padding: '3px 7px', borderRadius: 6,
        }}>SL</span>
        <span style={{
          fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
          background: '#efe7fb', color: '#7a5bbf', padding: '3px 7px', borderRadius: 6,
        }}>HL</span>
      </header>

      {/* ── Two-column layout ─────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: '1rem',
        padding: '1rem 1.25rem',
        maxWidth: 1280,
        margin: '0 auto',
        alignItems: 'start',
      }}>

        {/* ── LEFT COLUMN ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

          {/* Preset picker */}
          <div style={card}>
            <Label>PRELOADED PROGRAMS</Label>
            <select value={presetIdx} onChange={e => selectPreset(Number(e.target.value))}
              style={selectStyle}>
              {PRELOADED.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
            </select>
            <p style={{ fontSize: '0.72rem', color: B.faint, margin: '0.45rem 0 0', lineHeight: 1.55 }}>
              {PRELOADED[presetIdx].desc}
            </p>
          </div>

          {/* Code editor */}
          <div style={card}>
            <Label>ASSEMBLY CODE</Label>
            <textarea
              value={source}
              onChange={e => setSource(e.target.value)}
              rows={10}
              spellCheck={false}
              aria-label="Assembly code editor"
              style={{
                width: '100%', padding: '0.55rem 0.7rem', borderRadius: 6,
                border: `1px solid ${B.borderStrong}`,
                fontFamily: mono, fontSize: '0.83rem', color: B.text,
                background: B.bg, resize: 'vertical', outline: 'none',
                lineHeight: 1.75, boxSizing: 'border-box',
              }}
            />
            {assembleError && (
              <div style={{
                fontSize: '0.76rem', color: '#dc2626', marginTop: '0.3rem',
                padding: '0.3rem 0.5rem', background: '#fef2f2', borderRadius: 4,
              }}>{assembleError}</div>
            )}
            <button onClick={handleAssemble} style={{
              marginTop: '0.5rem', padding: '0.5rem 1rem', width: '100%',
              background: B.green, color: '#fff', border: 'none', borderRadius: 7,
              fontSize: '0.82rem', fontWeight: 800, fontFamily: font,
              cursor: 'pointer', transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.background = B.green}
            >ASSEMBLE</button>
          </div>

          {/* Assembled listing */}
          {assembled && (
            <div style={card}>
              <Label>ASSEMBLED LISTING</Label>
              <div style={{ fontFamily: mono, fontSize: '0.77rem', maxHeight: 186, overflowY: 'auto' }}>
                {assembled.listing.map(row => {
                  const isPC = currentState?.pc === row.addr;
                  return (
                    <div key={row.addr} style={{
                      display: 'grid', gridTemplateColumns: '22px 36px 30px 1fr', gap: 4,
                      padding: '2px 4px', borderRadius: 3,
                      background: isPC ? '#e8f7ef' : 'transparent',
                      color: isPC ? B.greenDark : B.text,
                    }}>
                      <span style={{ color: isPC ? B.greenDark : B.faint }}>{pad2(row.addr)}</span>
                      <span style={{ fontWeight: isPC ? 700 : 400 }}>{row.mnemonic}</span>
                      <span style={{ color: B.muted }}>{row.operandStr}</span>
                      <span style={{ color: B.faint, textAlign: 'right' }}>{pad3(row.value)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Input panel */}
          <div style={card}>
            <Label>INPUT QUEUE <span style={{ fontWeight: 400, textTransform: 'none' }}>(space-separated)</span></Label>
            <input type="text" value={inputText} onChange={e => setInputText(e.target.value)}
              placeholder="e.g. 5 12"
              aria-label="Input queue"
              style={{
                width: '100%', padding: '0.38rem 0.6rem', borderRadius: 6,
                border: `1px solid ${B.borderStrong}`, fontFamily: mono,
                fontSize: '0.85rem', color: B.text, background: B.bg,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            {currentState?.inputQueue?.length > 0 && (
              <div style={{ marginTop: '0.3rem', fontSize: '0.73rem', color: B.muted }}>
                Remaining: {currentState.inputQueue.join(', ')}
              </div>
            )}
          </div>

          {/* Output log */}
          <div style={card}>
            <Label>OUTPUT</Label>
            <div style={{
              fontFamily: mono, fontSize: '0.92rem', color: B.text,
              minHeight: 34, padding: '0.38rem 0.5rem', background: B.bg,
              borderRadius: 6, border: `1px solid ${B.border}`,
            }}>
              {currentState?.outputLog?.length
                ? currentState.outputLog.join(', ')
                : <span style={{ color: B.faint }}>·</span>}
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

          {/* Control bar */}
          <div style={{
            ...card, display: 'flex', alignItems: 'center',
            gap: '0.45rem', flexWrap: 'wrap',
          }}>
            {currentStep && <StageIndicator stage={currentStep.stage} />}
            <div style={{ flex: 1 }} />

            <button onClick={doBack} disabled={!canBack} style={btn(false, !canBack)} aria-label="Step backward">
              ← Back
            </button>
            <button onClick={doMicroStep} disabled={!canStep} style={btn(false, !canStep)}>
              Step
            </button>
            <button onClick={doInstrStep} disabled={!canStep} style={btn(false, !canStep)}>
              Step instr.
            </button>
            <button
              onClick={() => setRunning(r => !r)}
              disabled={!canStep && !running}
              style={btn(true, !canStep && !running)}
            >
              {running ? '⏸ Pause' : '▶ Run'}
            </button>
            <button onClick={doReset} disabled={!snapshots.length} style={btn(false, !snapshots.length)}>
              Reset
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 4 }}>
              <span style={{ fontSize: '0.7rem', color: B.faint, fontWeight: 600 }}>Speed</span>
              <input type="range" min={100} max={1500} step={100}
                value={1600 - speed}
                onChange={e => setSpeed(1600 - Number(e.target.value))}
                style={{ width: 68, accentColor: B.green }}
                aria-label="Playback speed"
              />
            </div>

            {steps.length > 0 && (
              <span style={{ fontSize: '0.7rem', color: B.faint, marginLeft: 2 }}>
                {stepIdx}/{steps.length}
              </span>
            )}
            {isHalted && (
              <span style={{ fontSize: '0.76rem', color: '#dc2626', fontWeight: 800 }}>HALTED</span>
            )}
          </div>

          {/* Register summary strip */}
          {currentState && (
            <div style={{ ...card, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { label: 'PC',  id: 'reg-pc',  val: pad2(currentState.pc),  title: 'Program Counter' },
                { label: 'MAR', id: 'reg-mar', val: pad2(currentState.mar), title: 'Memory Address Register' },
                { label: 'MDR', id: 'reg-mdr', val: pad3(currentState.mdr), title: 'Memory Data Register' },
                { label: 'IR',  id: 'reg-ir',  val: pad3(currentState.ir),  title: 'Instruction Register' },
                { label: 'AC',  id: 'reg-acc', val: currentState.acc,       title: 'Accumulator' },
              ].map(({ label, id, val, title }) => {
                const on = currentStep?.highlights?.includes(id);
                return (
                  <div key={label} title={title} style={{
                    textAlign: 'center', minWidth: 50,
                    padding: '0.2rem 0.45rem', borderRadius: 5,
                    background: on ? B.greenSoft : 'transparent',
                    border: `1px solid ${on ? B.green : 'transparent'}`,
                    transition: 'all 0.2s',
                  }}>
                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: on ? B.greenDark : B.faint, fontFamily: font }}>{label}</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 800, color: on ? B.greenDark : B.text, fontFamily: mono }}>{val}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SVG diagram */}
          <div style={{ ...card, padding: '0.6rem' }}>
            <CPUDiagram
              state={currentState || blankState}
              highlights={currentStep?.highlights || []}
              busActivity={currentStep?.busActivity || null}
            />
          </div>

          {/* Narration — aria-live region */}
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              background: B.greenSoft, border: `1px solid ${B.green}`,
              borderRadius: 10, padding: '0.75rem 1rem',
              fontSize: '0.875rem', color: B.greenDark,
              fontFamily: font, fontWeight: 600, lineHeight: 1.65, minHeight: 52,
            }}
          >
            {currentStep
              ? currentStep.narration
              : assembled
                ? `Ready: ${steps.length} micro-steps assembled. Press Step or Run to begin.`
                : 'Write or choose a program, then press Assemble.'}
          </div>

        </div>
      </div>

      {/* ── Register reference card ───────────────────────────────────── */}
      <div style={{ maxWidth: 1280, margin: '0 auto 2rem', padding: '0 1.25rem' }}>
        <details style={{ ...card }}>
          <summary style={{ fontSize: '0.8rem', fontWeight: 700, color: B.muted, cursor: 'pointer', fontFamily: font }}>
            Register &amp; bus reference
          </summary>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: '0.5rem', marginTop: '0.75rem',
          }}>
            {[
              { abbr: 'PC',  name: 'Program Counter',           desc: 'Holds the address of the next instruction to fetch.' },
              { abbr: 'MAR', name: 'Memory Address Register',   desc: 'Holds the address to be read from or written to. Travels on the address bus.' },
              { abbr: 'MDR', name: 'Memory Data Register',      desc: 'Holds data being transferred to or from memory via the data bus.' },
              { abbr: 'IR',  name: 'Instruction Register',      desc: 'Holds the instruction being decoded and executed.' },
              { abbr: 'AC',  name: 'Accumulator',               desc: 'The working register for all arithmetic and logic operations.' },
              { abbr: 'CU',  name: 'Control Unit',              desc: 'Directs the fetch-decode-execute cycle and sends read/write signals.' },
              { abbr: 'ALU', name: 'Arithmetic Logic Unit',     desc: 'Performs ADD and SUB on the accumulator and sets the negative flag.' },
            ].map(({ abbr, name, desc }) => (
              <div key={abbr} style={{ padding: '0.5rem 0.6rem', background: B.bg, borderRadius: 6 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: B.green, fontFamily: font }}>{abbr}</div>
                <div style={{ fontSize: '0.73rem', fontWeight: 700, color: B.text, fontFamily: font }}>{name}</div>
                <div style={{ fontSize: '0.68rem', color: B.muted, fontFamily: font, lineHeight: 1.5, marginTop: 2 }}>{desc}</div>
              </div>
            ))}
          </div>
        </details>

        <details style={{ ...card, marginTop: '0.5rem' }}>
          <summary style={{ fontSize: '0.8rem', fontWeight: 700, color: B.muted, cursor: 'pointer', fontFamily: font }}>
            Instruction set reference
          </summary>
          <table style={{
            width: '100%', marginTop: '0.75rem', borderCollapse: 'collapse',
            fontFamily: font, fontSize: '0.78rem',
          }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${B.border}` }}>
                {['Mnemonic', 'Code', 'Operation', 'Notes'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '0.3rem 0.6rem',
                    color: B.muted, fontWeight: 700, fontSize: '0.7rem',
                    letterSpacing: '0.4px',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { mn: 'ADD xx', code: '1xx', op: 'ACC = ACC + memory[xx]',   note: 'Reads via data bus; sets negative flag if result < 0' },
                { mn: 'SUB xx', code: '2xx', op: 'ACC = ACC − memory[xx]',   note: 'Sets negative flag if result < 0' },
                { mn: 'STA xx', code: '3xx', op: 'memory[xx] = ACC',         note: 'Only instruction that writes to memory; data bus runs CPU→memory' },
                { mn: 'LDA xx', code: '5xx', op: 'ACC = memory[xx]',         note: 'Loads a value without arithmetic' },
                { mn: 'BRA xx', code: '6xx', op: 'PC = xx',                  note: 'Unconditional branch' },
                { mn: 'BRZ xx', code: '7xx', op: 'if ACC = 0 then PC = xx',  note: 'Branch if accumulator is zero' },
                { mn: 'BRP xx', code: '8xx', op: 'if ACC ≥ 0 then PC = xx',  note: 'Branch if negative flag is not set' },
                { mn: 'INP',    code: '901', op: 'ACC = next input',          note: 'I/O; does not use the address or data bus' },
                { mn: 'OUT',    code: '902', op: 'output ACC',                note: 'I/O; does not use the address or data bus' },
                { mn: 'HLT',    code: '000', op: 'halt',                      note: 'Stops the machine' },
                { mn: 'DAT n',  code: 'n/a', op: 'reserve a mailbox',         note: 'Assembler directive only; optional initial value n (default 0)' },
              ].map(({ mn, code, op, note }, i) => (
                <tr key={mn} style={{ background: i % 2 === 0 ? B.bg : 'transparent' }}>
                  <td style={{ padding: '0.38rem 0.6rem', fontFamily: mono, fontWeight: 700, color: B.green }}>{mn}</td>
                  <td style={{ padding: '0.38rem 0.6rem', fontFamily: mono, color: B.muted }}>{code}</td>
                  <td style={{ padding: '0.38rem 0.6rem', color: B.text }}>{op}</td>
                  <td style={{ padding: '0.38rem 0.6rem', color: B.faint, fontSize: '0.72rem' }}>{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>

    </div>
  );
}

// ── Shared inline styles ──────────────────────────────────────────────────────

const card = {
  background: B.surface, borderRadius: 12,
  border: `1px solid ${B.border}`, padding: '0.875rem',
};

const selectStyle = {
  width: '100%', padding: '0.38rem 0.55rem',
  border: `1px solid ${B.borderStrong}`, borderRadius: 6,
  fontSize: '0.84rem', fontFamily: font, color: B.text,
  background: B.bg, cursor: 'pointer',
};

function Label({ children }) {
  return (
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: B.muted, letterSpacing: '0.5px', marginBottom: '0.4rem', fontFamily: font }}>
      {children}
    </div>
  );
}
